import apiClient from '../../../api/client';
import { ReemitirPreviewSchema } from '@aid/shared';
import type {
  CobroClienteResponse,
  CobroStatsResponse,
  CreateCobroDto,
  UpdateCobroDto,
  QueryCobrosDto,
  GenerarCobrosDto,
  GenerarCobrosResult,
  CobroGeneracionDetail,
  CobroGeneracionScheduleContext,
  QueryCobroGeneracionesDto,
  CobroGeneracionSummary,
  AnularCobroDto,
  ReemitirCobroDto,
  ReemitirCobroResult,
  ReemitirPreview,
  Paginated,
} from '@aid/shared';

export type {
  CobroClienteResponse,
  CobroStatsResponse,
  CreateCobroDto,
  UpdateCobroDto,
  QueryCobrosDto,
  GenerarCobrosDto,
  AnularCobroDto,
  ReemitirCobroDto,
  ReemitirCobroResult,
  ReemitirPreview,
  CobroGeneracionDetail,
  CobroGeneracionScheduleContext,
  QueryCobroGeneracionesDto,
  CobroGeneracionSummary,
};

export async function getCobros(params: QueryCobrosDto): Promise<Paginated<CobroClienteResponse>> {
  const { data } = await apiClient.get<Paginated<CobroClienteResponse>>('/admin/cobros', {
    params,
  });
  return data;
}

export async function getCobrosStats(
  params: Omit<QueryCobrosDto, 'offset' | 'limit'>,
): Promise<CobroStatsResponse> {
  const { data } = await apiClient.get<CobroStatsResponse>('/admin/cobros/stats', { params });
  return data;
}

export async function getCobro(id: number): Promise<CobroClienteResponse> {
  const { data } = await apiClient.get<CobroClienteResponse>(`/admin/cobros/${id}`);
  return data;
}

export async function createCobro(dto: CreateCobroDto): Promise<CobroClienteResponse> {
  const { data } = await apiClient.post<CobroClienteResponse>('/admin/cobros', dto);
  return data;
}

export async function updateCobro(id: number, dto: UpdateCobroDto): Promise<CobroClienteResponse> {
  const { data } = await apiClient.patch<CobroClienteResponse>(`/admin/cobros/${id}`, dto);
  return data;
}

export async function deleteCobro(id: number): Promise<void> {
  await apiClient.delete(`/admin/cobros/${id}`);
}

export async function anularCobro(id: number, dto: AnularCobroDto): Promise<CobroClienteResponse> {
  const { data } = await apiClient.post<CobroClienteResponse>(`/admin/cobros/${id}/anular`, dto);
  return data;
}

/** Read-only re-issue preview (spec IR-W4); response is zod-validated. */
export async function reemitirCobroPreview(id: number): Promise<ReemitirPreview> {
  const { data } = await apiClient.post<unknown>(`/admin/cobros/${id}/reemitir/preview`);
  return ReemitirPreviewSchema.parse(data);
}

/** Atomic re-issue command (spec IR-W6); returns the terminal result. */
export async function reemitirCobro(id: number, dto: ReemitirCobroDto): Promise<ReemitirCobroResult> {
  const { data } = await apiClient.post<ReemitirCobroResult>(`/admin/cobros/${id}/reemitir`, dto);
  return data;
}

export async function generarCobros(dto: GenerarCobrosDto): Promise<GenerarCobrosResult> {
  const { data } = await apiClient.post<GenerarCobrosResult>('/admin/cobros/generar', dto);
  return data;
}

export async function recuperarCobros(): Promise<GenerarCobrosResult> {
  const { data } = await apiClient.post<GenerarCobrosResult>('/admin/cobros/generar/recuperar');
  return data;
}

export async function getCobrosGeneraciones(
  params: QueryCobroGeneracionesDto,
): Promise<Paginated<CobroGeneracionSummary>> {
  const { data } = await apiClient.get<Paginated<CobroGeneracionSummary>>(
    '/admin/cobros/generaciones',
    { params },
  );
  return data;
}

export async function getCobroGeneracionSchedule(): Promise<CobroGeneracionScheduleContext> {
  const { data } = await apiClient.get<CobroGeneracionScheduleContext>(
    '/admin/cobros/generaciones/schedule',
  );
  return data;
}

export async function getCobroGeneracion(id: number): Promise<CobroGeneracionDetail> {
  const { data } = await apiClient.get<CobroGeneracionDetail>(`/admin/cobros/generaciones/${id}`);
  return data;
}
